import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, 
  UserX,
  FileWarning,
  Plus,
  Loader2,
  Settings,
  Save
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

const INTERVENTION_TYPES = [
  { value: 'warning', label: 'Warning' },
  { value: 'support', label: 'Support Session' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'counseling', label: 'Counseling' },
  { value: 'other', label: 'Other' }
];

const DROPOUT_REASONS = [
  { value: 'personal', label: 'Personal Reasons' },
  { value: 'financial', label: 'Financial Reasons' },
  { value: 'academic', label: 'Academic Difficulty' },
  { value: 'work', label: 'Work Commitments' },
  { value: 'health', label: 'Health Issues' },
  { value: 'other', label: 'Other' }
];

export default function InterventionStep({ programmeId, programme, token, headers }) {
  const [atRiskLearners, setAtRiskLearners] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [dropoffs, setDropoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntervention, setShowIntervention] = useState(false);
  const [showDropoff, setShowDropoff] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [config, setConfig] = useState({
    progress_threshold: 30,
    days_inactive_threshold: 14
  });

  const [interventionForm, setInterventionForm] = useState({
    intervention_type: 'support',
    notes: '',
    follow_up_date: ''
  });

  const [dropoffForm, setDropoffForm] = useState({
    reason: 'personal',
    additional_notes: ''
  });

  useEffect(() => {
    if (programmeId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  const fetchData = async () => {
    try {
      const [atRiskRes, interventionsRes, dropoffsRes] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}/at-risk-learners`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/interventions`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/dropoffs`, { headers })
      ]);
      
      setAtRiskLearners(atRiskRes.data.at_risk_learners || []);
      setInterventions(interventionsRes.data.interventions || []);
      setDropoffs(dropoffsRes.data.dropoffs || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/programmes/${programmeId}/at-risk-config`, config, { headers });
      toast.success('At-risk thresholds updated');
      setShowConfig(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update config');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordIntervention = async () => {
    if (!selectedEmail || !interventionForm.notes) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/programmes/${programmeId}/interventions`, {
        learner_email: selectedEmail,
        ...interventionForm
      }, { headers });
      
      toast.success('Intervention recorded');
      setShowIntervention(false);
      setInterventionForm({ intervention_type: 'support', notes: '', follow_up_date: '' });
      setSelectedEmail('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record intervention');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordDropoff = async () => {
    if (!selectedEmail) {
      toast.error('Please select a learner');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/programmes/${programmeId}/dropoffs`, {
        learner_email: selectedEmail,
        ...dropoffForm
      }, { headers });
      
      toast.success('Dropout recorded');
      setShowDropoff(false);
      setDropoffForm({ reason: 'personal', additional_notes: '' });
      setSelectedEmail('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record dropout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAtRisk = async (email) => {
    try {
      await axios.post(`${API}/programmes/${programmeId}/remove-at-risk?email=${email}`, {}, { headers });
      toast.success('At-risk status removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove at-risk status');
    }
  };

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to manage interventions</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
        <p className="text-sm text-red-300">
          Track at-risk learners based on progress and activity thresholds. Record interventions, 
          support sessions, and manage learner dropoffs with reasons and dates.
        </p>
      </div>

      {/* Config Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setShowConfig(true)}
          className="border-zinc-700 text-zinc-300"
        >
          <Settings className="h-4 w-4 mr-2" />
          Configure At-Risk Thresholds
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="at-risk" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-zinc-800">
          <TabsTrigger value="at-risk" className="data-[state=active]:bg-zinc-700">
            At Risk ({atRiskLearners.length})
          </TabsTrigger>
          <TabsTrigger value="interventions" className="data-[state=active]:bg-zinc-700">
            Interventions ({interventions.length})
          </TabsTrigger>
          <TabsTrigger value="dropoffs" className="data-[state=active]:bg-zinc-700">
            Dropoffs ({dropoffs.length})
          </TabsTrigger>
        </TabsList>

        {/* At Risk Tab */}
        <TabsContent value="at-risk" className="mt-4">
          {atRiskLearners.length === 0 ? (
            <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
              <AlertTriangle className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No at-risk learners detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {atRiskLearners.map((learner) => (
                <Card key={learner.enrollment_id} className="bg-zinc-800/50 border-red-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">
                          {learner.first_name} {learner.last_name}
                        </p>
                        <p className="text-sm text-zinc-400">{learner.email}</p>
                        <p className="text-xs text-red-400 mt-1">{learner.at_risk_reason}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-500/20 text-red-400">
                          {learner.overall_progress || 0}% Progress
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedEmail(learner.email);
                            setShowIntervention(true);
                          }}
                          className="border-zinc-700 text-zinc-300"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Intervene
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAtRisk(learner.email)}
                          className="text-zinc-400 hover:text-white"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Interventions Tab */}
        <TabsContent value="interventions" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setShowIntervention(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Record Intervention
            </Button>
          </div>

          {interventions.length === 0 ? (
            <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
              <FileWarning className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No interventions recorded</p>
            </div>
          ) : (
            <div className="space-y-3">
              {interventions.map((intervention) => (
                <Card key={intervention.intervention_id} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{intervention.learner_email}</p>
                          <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                            {INTERVENTION_TYPES.find(t => t.value === intervention.intervention_type)?.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-400 mt-2">{intervention.notes}</p>
                        {intervention.follow_up_date && (
                          <p className="text-xs text-amber-400 mt-2">
                            Follow-up: {new Date(intervention.follow_up_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-zinc-500">
                        {new Date(intervention.recorded_at).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Dropoffs Tab */}
        <TabsContent value="dropoffs" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setShowDropoff(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <UserX className="h-4 w-4 mr-2" />
              Record Dropout
            </Button>
          </div>

          {dropoffs.length === 0 ? (
            <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
              <UserX className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No dropouts recorded</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dropoffs.map((dropoff) => (
                <Card key={dropoff.dropoff_id} className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-white">
                          {dropoff.learner_name || dropoff.learner_email}
                        </p>
                        <p className="text-sm text-zinc-400">{dropoff.learner_email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-red-500/20 text-red-400">
                            {DROPOUT_REASONS.find(r => r.value === dropoff.reason)?.label}
                          </Badge>
                          <span className="text-xs text-zinc-500">
                            Progress at drop: {dropoff.progress_at_drop}%
                          </span>
                        </div>
                        {dropoff.additional_notes && (
                          <p className="text-sm text-zinc-400 mt-2">{dropoff.additional_notes}</p>
                        )}
                      </div>
                      <span className="text-xs text-zinc-500">
                        {new Date(dropoff.recorded_at).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Configure At-Risk Thresholds</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Progress Threshold (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={config.progress_threshold}
                onChange={(e) => setConfig({ ...config, progress_threshold: parseInt(e.target.value) || 30 })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Learners below this progress are marked at-risk
              </p>
            </div>
            <div>
              <Label className="text-zinc-300">Inactive Days Threshold</Label>
              <Input
                type="number"
                min={1}
                value={config.days_inactive_threshold}
                onChange={(e) => setConfig({ ...config, days_inactive_threshold: parseInt(e.target.value) || 14 })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Learners inactive for this many days are marked at-risk
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)} className="border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={submitting} className="bg-teal-600 hover:bg-teal-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Intervention Dialog */}
      <Dialog open={showIntervention} onOpenChange={setShowIntervention}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Record Intervention</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Learner Email *</Label>
              <Input
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                placeholder="learner@example.com"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Intervention Type</Label>
              <Select
                value={interventionForm.intervention_type}
                onValueChange={(value) => setInterventionForm({ ...interventionForm, intervention_type: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {INTERVENTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Notes *</Label>
              <Textarea
                value={interventionForm.notes}
                onChange={(e) => setInterventionForm({ ...interventionForm, notes: e.target.value })}
                placeholder="Describe the intervention..."
                rows={3}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Follow-up Date (optional)</Label>
              <Input
                type="date"
                value={interventionForm.follow_up_date}
                onChange={(e) => setInterventionForm({ ...interventionForm, follow_up_date: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIntervention(false)} className="border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button onClick={handleRecordIntervention} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record Intervention'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dropoff Dialog */}
      <Dialog open={showDropoff} onOpenChange={setShowDropoff}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Record Dropout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Learner Email *</Label>
              <Input
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                placeholder="learner@example.com"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Reason</Label>
              <Select
                value={dropoffForm.reason}
                onValueChange={(value) => setDropoffForm({ ...dropoffForm, reason: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {DROPOUT_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Additional Notes</Label>
              <Textarea
                value={dropoffForm.additional_notes}
                onChange={(e) => setDropoffForm({ ...dropoffForm, additional_notes: e.target.value })}
                placeholder="Any additional details..."
                rows={3}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDropoff(false)} className="border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button onClick={handleRecordDropoff} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record Dropout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
